import React, { useCallback, useEffect, useRef, useState } from "react";

import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import useFontSize from "../../hooks/useFontSize";
import apiService from "../../services/api";
import { isPremiumUser } from "../../utils/premiumUtils";
import watchProgressUtils from "../../utils/watchProgressUtils";
import webPlayerStorage from "../../utils/webPlayerStorage";
import LeftShadow from "../LeftShadow";
import ManageCategories from "../ManageCategories";
import PremiumOverlay from "../PremiumOverlay";
import Top10Carousel from "../Top10Carousel";

import "./MediaBrowse.css";
import {
  ALL_CATEGORY,
  ALL_GENRE,
  MIN_SEARCH_LENGTH,
  SEARCH_DEBOUNCE_MS,
  SORT_OPTIONS,
  TOOLBAR_REVEAL_MS,
  browseConfig,
  createBrowseState,
  isCategoryLocked,
  mapMediaItem,
  mediaQuery,
  nextSort,
  normalizeCategories,
  pinMatches,
  reduceBrowse,
  showGenrePill,
  showLibraryPills,
  showPopular,
} from "./mediaBrowseModel";
import RottenTomatoesRow from "./RottenTomatoesRow";

function readLocks() {
  try {
    return JSON.parse(localStorage.getItem("locked_categories") || "{}");
  } catch {
    return {};
  }
}

function readPage(response, reset, requestedPage, mediaType) {
  const raw = Array.isArray(response?.data)
    ? response.data
    : response?.data?.movies || response?.data?.series || [];
  const media = watchProgressUtils.enrichItemsWithProgress(
    raw.map((item) => mapMediaItem(item, mediaType))
  );
  const popular = reset
    ? watchProgressUtils
        .enrichItemsWithProgress((response?.popular || []).map(mapMediaItem))
        .slice(0, 10)
    : undefined;

  return {
    media,
    popular,
    totalItems: response?.meta?.totalItems || 0,
    totalPages: response?.meta?.totalPages || 1,
    page: response?.meta?.currentPage || requestedPage,
  };
}

const MediaBrowse = ({
  mediaType = "movies",
  playlist,
  profile,
  onOpenWatched,
  onOpenCollections,
}) => {
  useFontSize();
  const config = browseConfig(mediaType);
  const configRef = useRef(config);

  configRef.current = config;
  const navigate = useNavigate();
  const [state, setState] = useState(createBrowseState);
  const [toolbarVisible, setToolbarVisible] = useState(false);
  const [openMenu, setOpenMenu] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchDraft, setSearchDraft] = useState("");
  const [manageOpen, setManageOpen] = useState(false);
  const [premiumOpen, setPremiumOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [pinPrompt, setPinPrompt] = useState(null);
  const [pinDigits, setPinDigits] = useState(["", "", "", ""]);
  const [pinError, setPinError] = useState(false);
  const [unlockedIds, setUnlockedIds] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const requestRef = useRef(0);
  const searchTimerRef = useRef(null);
  const sentinelRef = useRef(null);
  const stateRef = useRef(state);
  const pinRefs = useRef([]);

  stateRef.current = state;
  const isAuthenticated = !!webPlayerStorage.getToken();
  const locks = readLocks();

  const revealToolbar = useCallback(() => setToolbarVisible(true), []);

  const fetchPage = useCallback(async (nextState, reset) => {
    const requestId = ++requestRef.current;

    try {
      const query = new URLSearchParams(
        mediaQuery(nextState, reset ? 1 : nextState.page)
      );
      const response = await apiService.webPlayerRequest(
        `${configRef.current.endpoint}?${query}`
      );

      if (requestId !== requestRef.current) return;

      const page = readPage(
        response,
        reset,
        reset ? 1 : nextState.page,
        configRef.current.mediaType
      );

      setState((current) =>
        reduceBrowse(current, {
          type: "mediaPage",
          reset,
          ...page,
          popular: reset ? page.popular : current.popular,
        })
      );
      setLoadingMore(false);
    } catch (error) {
      if (requestId !== requestRef.current) return;
      setLoadingMore(false);
      setState((current) => {
        const failed = reduceBrowse(current, {
          type: "fail",
          message:
            error.message ||
            `Unable to load ${configRef.current.title.toLowerCase()}`,
        });

        if (failed.status !== "error") {
          const prefix = nextState.search.active
            ? "Search failed: "
            : "Error: ";

          toast.error(
            `${prefix}${error.message || `Unable to load ${configRef.current.title.toLowerCase()}`}`
          );
        }

        return failed;
      });
    }
  }, []);

  const fetchGenres = useCallback(
    async (category) => {
      const query = category?.id
        ? `?categoryIds=${encodeURIComponent(category.id)}`
        : "";

      try {
        const response = await apiService.webPlayerRequest(
          `${configRef.current.endpoint}/genres${query}`
        );

        setState((current) =>
          reduceBrowse(current, {
            type: "genres",
            genres: normalizeCategories(response?.data),
          })
        );
        revealToolbar();
      } catch {
        revealToolbar();
      }
    },
    [revealToolbar]
  );

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(revealToolbar, TOOLBAR_REVEAL_MS);

    const load = async () => {
      try {
        const initial = createBrowseState();
        const [categoriesResponse, genresResponse, mediaResponse] =
          await Promise.all([
            apiService.webPlayerRequest(
              `${configRef.current.endpoint}/categories`
            ),
            apiService.webPlayerRequest(`${configRef.current.endpoint}/genres`),
            apiService.webPlayerRequest(
              `${configRef.current.endpoint}?${new URLSearchParams(mediaQuery(initial, 1))}`
            ),
          ]);

        if (cancelled) return;

        const page = readPage(
          mediaResponse,
          true,
          1,
          configRef.current.mediaType
        );

        setState((current) => {
          const withLists = reduceBrowse(
            reduceBrowse(current, {
              type: "categories",
              categories: normalizeCategories(categoriesResponse?.data),
            }),
            {
              type: "genres",
              genres: normalizeCategories(genresResponse?.data),
            }
          );

          return reduceBrowse(withLists, {
            type: "mediaPage",
            reset: true,
            ...page,
          });
        });
        revealToolbar();
      } catch (error) {
        if (cancelled) return;
        setState((current) =>
          reduceBrowse(current, {
            type: "fail",
            message:
              error.message ||
              `Unable to load ${configRef.current.title.toLowerCase()}`,
          })
        );
        revealToolbar();
      }
    };

    load();

    return () => {
      cancelled = true;
      clearTimeout(timer);
      clearTimeout(searchTimerRef.current);
    };
  }, [mediaType, revealToolbar]);

  const reloadFrom = useCallback(
    (nextState, { genres = false } = {}) => {
      setLoadingMore(false);
      fetchPage(nextState, true);
      if (genres) fetchGenres(nextState.category);
    },
    [fetchGenres, fetchPage]
  );

  const apply = (action, options) => {
    const next = reduceBrowse(stateRef.current, action);

    setState(next);
    if (options?.reload) reloadFrom(next, options);
    setOpenMenu("");
  };

  const chooseCategory = (category) => {
    if (
      isCategoryLocked(category, locks, unlockedIds) &&
      !pinMatches(category, "", locks)
    ) {
      setOpenMenu("");
      setPinDigits(["", "", "", ""]);
      setPinError(false);
      setPinPrompt(category);

      return;
    }

    const selectingAll = !category.id;

    apply(
      {
        type: "selectCategory",
        category: selectingAll ? ALL_CATEGORY : category,
      },
      { reload: true, genres: true }
    );
  };

  const submitPin = (digits) => {
    const pin = digits.join("");

    if (!pinMatches(pinPrompt, pin, locks)) {
      setPinError(true);
      setPinDigits(["", "", "", ""]);
      pinRefs.current[0]?.focus();

      return;
    }

    const unlocked = [...unlockedIds, String(pinPrompt.id)];

    setUnlockedIds(unlocked);
    setPinPrompt(null);
    apply(
      { type: "selectCategory", category: pinPrompt },
      { reload: true, genres: true }
    );
  };

  const submitSearch = () => {
    const query = searchDraft.trim();

    setSearchOpen(false);

    if (query.length < MIN_SEARCH_LENGTH) return;

    const next = reduceBrowse(stateRef.current, {
      type: "submitSearch",
      query,
    });

    setState(next);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      fetchPage(next, true);
    }, SEARCH_DEBOUNCE_MS);
  };

  const clearSearch = () => {
    setSearchDraft("");
    apply({ type: "clearSearch" }, { reload: true, genres: true });
  };

  const openMovie = (item) => {
    const movieId =
      item.mediaId && item.mediaId !== -1 ? item.mediaId : item.id;

    if (!movieId || movieId === -1) return;

    if (webPlayerStorage.getToken() && !isPremiumUser()) {
      setPremiumOpen(true);
      setContextMenu(null);

      return;
    }

    navigate(config.detailPath(movieId), {
      state: { playlist, profile, movie: item, series: item },
    });
  };

  const toggleWatchlist = async (item) => {
    setContextMenu(null);

    try {
      const bookmarkType = config.mediaType;

      if (item.isBookmarked) {
        await apiService.webPlayerDeleteBookmark(item.mediaId, bookmarkType);
      } else {
        await apiService.webPlayerCreateBookmark(item.mediaId, bookmarkType);
      }

      setState((current) => ({
        ...current,
        media: current.media.map((movie) =>
          movie.mediaId === item.mediaId
            ? { ...movie, isBookmarked: !item.isBookmarked }
            : movie
        ),
        popular: current.popular.map((movie) =>
          movie.mediaId === item.mediaId
            ? { ...movie, isBookmarked: !item.isBookmarked }
            : movie
        ),
      }));
    } catch (error) {
      toast.error(error.message || "Could not update the watchlist");
    }
  };

  const loadMore = useCallback(() => {
    const { current } = stateRef;

    if (loadingMore || current.search.pending || !current.hasMore) return;
    if (current.status !== "content") return;

    setLoadingMore(true);
    fetchPage({ ...current, page: current.page + 1 }, false);
  }, [fetchPage, loadingMore]);

  useEffect(() => {
    const node = sentinelRef.current;

    if (!node) return undefined;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) loadMore();
    });

    observer.observe(node);

    return () => observer.disconnect();
  }, [loadMore, state.media.length, state.status]);

  useEffect(() => {
    const close = () => {
      setOpenMenu("");
      setContextMenu(null);
    };
    const onKey = (event) => {
      if (event.key === "Escape") {
        setOpenMenu("");
        setSearchOpen(false);
        setContextMenu(null);
        setPinPrompt(null);
      }
    };

    window.addEventListener("click", close);
    window.addEventListener("keydown", onKey);

    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  const retry = () => {
    setState(createBrowseState());
    setToolbarVisible(false);
    window.location.reload();
  };

  const sortLabel =
    SORT_OPTIONS.find((option) => option.id === state.sort.option)?.label ||
    "Date Added";
  const emptySearch =
    state.search.active && !state.search.pending && state.media.length === 0;

  return (
    <section className="media-browse">
      <LeftShadow className="fixed inset-0 z-0" />
      <header className="media-browse-header">
        <h1 className="media-browse-title">
          {config.title}
          {state.totalItems > 0 && (
            <span className="media-browse-count">({state.totalItems})</span>
          )}
        </h1>
        <div
          className={`media-browse-pills${toolbarVisible ? " is-visible" : ""}`}
        >
          {state.status === "loading" && !toolbarVisible ? (
            <>
              <span className="media-shimmer pill" />
              <span className="media-shimmer pill" />
              <span className="media-shimmer pill" />
            </>
          ) : (
            <>
              <MenuPill
                icon="fa-arrow-down-wide-short"
                label={sortLabel}
                open={openMenu === "sort"}
                onToggle={() => setOpenMenu(openMenu === "sort" ? "" : "sort")}
              >
                {SORT_OPTIONS.map((option) => {
                  const selected = option.id === state.sort.option;

                  return (
                    <button
                      className={`glass-option${selected ? " is-selected" : ""}`}
                      key={option.id}
                      type="button"
                      onClick={() =>
                        apply(
                          {
                            type: "applySort",
                            sort: nextSort(state.sort, option.id),
                          },
                          { reload: true }
                        )
                      }
                    >
                      <span>{option.label}</span>
                      {selected && (
                        <i
                          className={`fas fa-arrow-${state.sort.direction === "asc" ? "up" : "down"}`}
                        />
                      )}
                    </button>
                  );
                })}
              </MenuPill>

              <MenuPill
                icon="fa-folder"
                label={state.category.name}
                open={openMenu === "category"}
                onToggle={() =>
                  setOpenMenu(openMenu === "category" ? "" : "category")
                }
              >
                {[ALL_CATEGORY, ...state.categories].map((category) => (
                  <button
                    className={`glass-option${category.id === state.category.id ? " is-selected" : ""}`}
                    key={category.id || "all"}
                    type="button"
                    onClick={() => chooseCategory(category)}
                  >
                    <span>{category.name}</span>
                    {isCategoryLocked(category, locks, unlockedIds) && (
                      <i className="fas fa-lock" />
                    )}
                  </button>
                ))}
              </MenuPill>

              {showGenrePill(state.genres) && (
                <MenuPill
                  icon="fa-masks-theater"
                  label={state.genre.name}
                  open={openMenu === "genre"}
                  onToggle={() =>
                    setOpenMenu(openMenu === "genre" ? "" : "genre")
                  }
                >
                  {[ALL_GENRE, ...state.genres].map((genre) => (
                    <button
                      className={`glass-option${genre.name === state.genre.name ? " is-selected" : ""}`}
                      key={genre.id || genre.name}
                      type="button"
                      onClick={() =>
                        apply({ type: "selectGenre", genre }, { reload: true })
                      }
                    >
                      {genre.name}
                    </button>
                  ))}
                </MenuPill>
              )}

              {config.showLibrary && showLibraryPills(isAuthenticated) && (
                <>
                  <button
                    className="glass-capsule"
                    type="button"
                    onClick={onOpenWatched}
                  >
                    <i className="fas fa-eye" />
                    Watched
                  </button>
                  <button
                    className="glass-capsule"
                    type="button"
                    onClick={onOpenCollections}
                  >
                    <i className="fas fa-layer-group" />
                    Collections
                  </button>
                </>
              )}

              {state.search.active ? (
                <button
                  className="glass-capsule"
                  type="button"
                  onClick={clearSearch}
                >
                  <i className="fas fa-times" />
                  Clear
                </button>
              ) : (
                <button
                  aria-label="Search"
                  className="glass-capsule is-icon"
                  type="button"
                  onClick={() => {
                    setSearchDraft("");
                    setSearchOpen(true);
                  }}
                >
                  <i className="fas fa-search" />
                </button>
              )}

              <button
                aria-label="Manage categories"
                className="glass-capsule is-icon"
                type="button"
                onClick={() => setManageOpen(true)}
              >
                <i className="fas fa-ellipsis" />
              </button>
            </>
          )}
        </div>
      </header>

      <div className="media-browse-stage">
        {state.status === "error" && (
          <div className="browse-error">
            <p>
              {state.error ||
                `Unable to load ${config.title.toLowerCase()}. Please try again.`}
            </p>
            <button className="glass-capsule" type="button" onClick={retry}>
              Try again
            </button>
          </div>
        )}

        {state.status === "loading" && (
          <div aria-hidden="true" className="media-grid">
            {Array.from({ length: 12 }, (_, index) => (
              <div className="media-shimmer poster" key={index} />
            ))}
          </div>
        )}

        {state.status === "content" && showPopular(state) && (
          <Top10Carousel
            bounded
            items={state.popular}
            title=""
            onItemClick={openMovie}
          />
        )}

        {state.status === "content" && emptySearch && (
          <div className="browse-empty">
            <i className="fas fa-search" />
            <p>{config.emptyMessage}</p>
          </div>
        )}

        {state.status === "content" && !emptySearch && (
          <div className="media-grid">
            {state.media.map((item) => (
              <div
                className="media-card lunova-card-host"
                key={item.mediaId || item.id}
              >
                <button
                  className="poster-card lunova-card"
                  type="button"
                  onClick={() => openMovie(item)}
                  onContextMenu={(event) => {
                    event.preventDefault();
                    setContextMenu({
                      item,
                      x: event.clientX,
                      y: event.clientY,
                    });
                  }}
                >
                  {item.posterPath ? (
                    <img alt={item.name || "Movie"} src={item.posterPath} />
                  ) : (
                    <span className="poster-fallback">{item.name}</span>
                  )}
                </button>
                <RottenTomatoesRow ratings={item.rottenTomatoes} />
              </div>
            ))}
          </div>
        )}

        {state.status === "content" && state.hasMore && !emptySearch && (
          <div className="browse-status" ref={sentinelRef}>
            {(loadingMore || state.search.pending) && <span>Loading...</span>}
          </div>
        )}
      </div>

      {searchOpen && (
        <div
          className="search-overlay"
          role="presentation"
          onClick={() => setSearchOpen(false)}
          onKeyDown={(event) => {
            if (event.key === "Escape") setSearchOpen(false);
          }}
        >
          <form
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            onSubmit={(event) => {
              event.preventDefault();
              submitSearch();
            }}
          >
            <input
              autoFocus
              aria-label="Type to search..."
              className="glass-field"
              placeholder="Type to search..."
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
            />
          </form>
        </div>
      )}

      {contextMenu && (
        <div
          className="glass-panel context-menu"
          role="menu"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <button
            className="glass-option"
            type="button"
            onClick={() => openMovie(contextMenu.item)}
          >
            View Details
          </button>
          <button
            className="glass-option"
            type="button"
            onClick={() => toggleWatchlist(contextMenu.item)}
          >
            {contextMenu.item.isBookmarked
              ? "Remove from Watchlist"
              : "Add to Watchlist"}
          </button>
        </div>
      )}

      {pinPrompt && (
        <div className="search-overlay">
          <div className="glass-panel pin-dialog">
            <h2>Enter PIN</h2>
            <p>Enter your PIN to open {pinPrompt.name}</p>
            <div className="pin-inputs">
              {pinDigits.map((digit, index) => (
                <input
                  className="glass-field"
                  inputMode="numeric"
                  key={index}
                  maxLength={1}
                  ref={(node) => {
                    pinRefs.current[index] = node;
                  }}
                  value={digit}
                  onChange={(event) => {
                    const value = event.target.value
                      .replace(/\D/g, "")
                      .slice(-1);
                    const next = [...pinDigits];

                    next[index] = value;
                    setPinDigits(next);
                    setPinError(false);
                    if (value && index < 3) pinRefs.current[index + 1]?.focus();
                    if (index === 3 && value) submitPin(next);
                  }}
                />
              ))}
            </div>
            {pinError && <p className="pin-error">Incorrect PIN</p>}
          </div>
        </div>
      )}

      <PremiumOverlay
        isOpen={premiumOpen}
        onClose={() => setPremiumOpen(false)}
      />
      <ManageCategories
        categories={state.categories}
        isOpen={manageOpen}
        profile={profile}
        onClose={() => setManageOpen(false)}
        onUpdate={() => {
          setManageOpen(false);
          fetchGenres(state.category);
          apiService
            .webPlayerRequest(`${config.endpoint}/categories`)
            .then((response) => {
              setState((current) =>
                reduceBrowse(current, {
                  type: "categories",
                  categories: normalizeCategories(response?.data),
                })
              );
            })
            .catch(() => {});
        }}
      />
    </section>
  );
};

const MenuPill = ({ icon, label, open, onToggle, children }) => {
  const buttonRef = useRef(null);
  const [place, setPlace] = useState(null);

  useEffect(() => {
    if (!open) return undefined;

    const update = () => {
      const rect = buttonRef.current?.getBoundingClientRect();

      if (!rect) return;
      setPlace({
        top: rect.bottom + 8,
        right: Math.max(8, window.innerWidth - rect.right),
      });
    };

    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);

    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open]);

  return (
    <div
      className="glass-menu"
      role="presentation"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <button
        aria-expanded={open}
        className="glass-capsule"
        ref={buttonRef}
        type="button"
        onClick={onToggle}
      >
        <i className={`fas ${icon}`} />
        <span>{label}</span>
        <i className={`fas fa-chevron-${open ? "up" : "down"} chevron`} />
      </button>
      {open && place && (
        <div
          className="glass-panel"
          style={{
            top: place.top,
            right: place.right,
            "--menu-top": `${place.top}px`,
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
};

export default MediaBrowse;
